import { useEffect, useState } from 'react'
import { adminApi } from '@/services/api'

interface UserOption {
  user_id: string
  name: string
}

interface UserMultiSelectProps {
  value: string[]
  onChange: (ids: string[]) => void
  disabled?: boolean
}

/**
 * Multi-Select für User-IDs (Konzept A — "kein Vertreter"-Empfängerliste).
        Zeigt Namen an, speichert IDs. Datenquelle: /admin/users/search.
 */
export function UserMultiSelect({ value, onChange, disabled }: UserMultiSelectProps) {
  const [selected, setSelected] = useState<UserOption[]>([])
  const [query, setQuery] = useState('')
  const [results, setResults] = useState<UserOption[]>([])
  const [loading, setLoading] = useState(false)

  // Resolve already-selected ids to names
  useEffect(() => {
    let alive = true
    if (!value || value.length === 0) {
      setSelected([])
      return
    }
    Promise.all(
      value.map(async id => {
        try {
          const res = await adminApi.searchUsers({ search: id, limit: 1 })
          const u = res?.users?.[0]
          return { user_id: id, name: u ? `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.email : id }
        } catch {
          return { user_id: id, name: id }
        }
      }),
    ).then(opts => alive && setSelected(opts))
    return () => {
      alive = false
    }
  }, [value])

  const runSearch = async (q: string) => {
    setQuery(q)
    if (q.trim().length < 2) {
      setResults([])
      return
    }
    setLoading(true)
    try {
      const res = await adminApi.searchUsers({ search: q, limit: 20 })
      const list = res?.users || []
      setResults(
        list.map(u => ({
          user_id: u.user_id,
          name: `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.email,
        })),
      )
    } catch {
      setResults([])
    } finally {
      setLoading(false)
    }
  }

  const add = (u: UserOption) => {
    if (value.includes(u.user_id)) return
    onChange([...value, u.user_id])
    setSelected(prev => [...prev, u])
    setQuery('')
    setResults([])
  }

  const remove = (id: string) => {
    onChange(value.filter(v => v !== id))
    setSelected(prev => prev.filter(s => s.user_id !== id))
  }

  return (
    <div className="w-full">
      <div className="flex flex-wrap gap-1.5 mb-2">
        {selected.map(s => (
          <span key={s.user_id} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-700 text-xs">
            {s.name}
            {!disabled && (
              <button onClick={() => remove(s.user_id)} className="text-indigo-400 hover:text-indigo-700">×</button>
            )}
          </span>
        ))}
        {selected.length === 0 && <span className="text-xs text-gray-400">Noch niemand ausgewählt (leer = alle Admins)</span>}
      </div>
      {!disabled && (
        <div className="relative">
          <input
            value={query}
            onChange={e => runSearch(e.target.value)}
            placeholder="Namen/Email suchen …"
            className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg"
          />
          {loading && <span className="absolute right-2 top-1.5 text-xs text-gray-400">…</span>}
          {results.length > 0 && (
            <div className="absolute z-10 mt-1 w-full max-h-48 overflow-y-auto bg-white border border-gray-200 rounded-lg shadow-lg">
              {results.map(u => (
                <button
                  key={u.user_id}
                  onClick={() => add(u)}
                  className="block w-full text-left px-3 py-1.5 text-sm hover:bg-indigo-50"
                >
                  {u.name}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
